import { motion } from 'framer-motion';
import { Check, X, ChevronDown } from 'lucide-react';
import { useState } from 'react';

const features = [
  {
    name: 'Page Speed',
    smartsite: '99/100',
    smartsiteMobile: '99/100 Score',
    others: '45-100',
    othersMobile: '45-100 Score',
    smartsiteGood: true,
    details: 'Our sites are built with performance-first architecture. Every millisecond counts for conversions and SEO rankings.',
  },
  {
    name: 'Security',
    smartsite: 'Bank-Grade SSL',
    smartsiteMobile: 'Bank-Grade SSL',
    others: 'Basic/None',
    othersMobile: 'Basic or None',
    smartsiteGood: true,
    details: 'Enterprise-level security with SSL certificates, DDoS protection, and regular security audits.',
  },
  {
    name: 'Technology',
    smartsite: 'React + Modern Stack',
    smartsiteMobile: 'React / Next.js',
    others: 'WordPress/Outdated',
    othersMobile: 'Standard WordPress',
    smartsiteGood: true,
    details: 'Built with the same technology used by Netflix, Airbnb, and Meta. Future-proof and lightning fast.',
  },
  {
    name: 'Local SEO',
    smartsite: 'Priority Care',
    smartsiteMobile: 'Priority Care',
    others: 'Extra Cost',
    othersMobile: 'Extra Cost',
    smartsiteGood: true,
    details: 'Google Business Profile optimisation, local citations, and area-specific landing pages included.',
  },
];

// Full feature list for desktop table
const allFeatures = [
  ...features,
  {
    name: 'Content Creation',
    smartsite: 'Done-For-You',
    smartsiteMobile: 'Done-4-You',
    others: 'DIY',
    othersMobile: 'DIY',
    smartsiteGood: true,
    details: 'We write all your copy, create your service pages, and handle ongoing blog content. You just approve.',
  },
  {
    name: 'AI Schema',
    smartsite: 'Advanced',
    smartsiteMobile: 'Advanced',
    others: 'None',
    othersMobile: 'None',
    smartsiteGood: true,
    details: 'Structured data that helps AI assistants like Siri and ChatGPT recommend your business.',
  },
  {
    name: 'Mobile Optimisation',
    smartsite: 'Full Responsive',
    smartsiteMobile: 'Responsive',
    others: 'Sometimes',
    othersMobile: 'Sometimes',
    smartsiteGood: true,
    details: '80% of your customers search on mobile. Our sites are designed mobile-first.',
  },
];

export function ComparisonSection() {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  return (
    <section className="py-16 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 network-grid opacity-20" />
      
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-muted-foreground mb-6">
            The SmartSite Difference
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Not All Websites Are{' '}
            <span className="text-gradient">Created Equal</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            See how we compare to typical web agencies and DIY solutions.
          </p>
        </motion.div>

        {/* Mobile Comparison Cards - Visible only on mobile */}
        <div className="block md:hidden space-y-4">
          {allFeatures.map((feature, index) => (
            <motion.div
              key={feature.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
            >
              {/* Feature Name Header */}
              <h3 className="text-lg font-bold text-white mb-5">{feature.name}</h3>
              
              {/* Two Column Comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Left Side - SmartSite (Winner) */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-xs text-muted-foreground">SmartSite</span>
                  </div>
                  <p className="text-sm font-semibold text-emerald-400 pl-8">
                    {feature.smartsiteMobile}
                  </p>
                </div>
                
                {/* Right Side - Others (Loser) */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                      <X className="w-4 h-4 text-red-400" />
                    </div>
                    <span className="text-xs text-muted-foreground">Others</span>
                  </div>
                  <p className="text-sm text-muted-foreground pl-8">
                    {feature.othersMobile}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Desktop Comparison Table - Hidden on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="hidden md:block glass-card overflow-hidden max-w-4xl mx-auto"
        >
          {/* Header Row */}
          <div className="grid grid-cols-[1fr_1.2fr_1fr] bg-white/5 border-b border-white/10">
            <div className="p-3 lg:p-6 font-medium text-muted-foreground text-xs lg:text-base flex items-center">Feature</div>
            <div className="p-3 lg:p-6 flex items-center justify-center">
              <span className="font-display font-bold text-gradient text-sm lg:text-lg">SmartSite</span>
            </div>
            <div className="p-3 lg:p-6 flex items-center justify-center font-medium text-muted-foreground text-xs lg:text-base">Others</div>
          </div>
          
          {/* Feature Rows */}
          {allFeatures.map((feature, index) => (
            <div key={feature.name}>
              <button
                onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                className="w-full grid grid-cols-[1fr_1.2fr_1fr] border-b border-white/5 hover:bg-white/5 transition-colors text-left"
              >
                <div className="p-3 py-4 lg:p-6 flex items-center gap-1.5 min-w-0">
                  <span className="font-medium text-xs lg:text-base leading-tight">{feature.name}</span>
                  <ChevronDown 
                    className={`w-3 h-3 lg:w-4 lg:h-4 text-muted-foreground transition-transform flex-shrink-0 ${
                      expandedRow === index ? 'rotate-180' : ''
                    }`} 
                  />
                </div>
                <div className="p-3 py-4 lg:p-6 flex items-center justify-center gap-1.5 lg:gap-2">
                  <Check className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-emerald-400 font-medium text-xs lg:text-base text-center leading-tight">
                    {feature.smartsite}
                  </span>
                </div>
                <div className="p-3 py-4 lg:p-6 flex items-center justify-center gap-1.5 lg:gap-2 text-muted-foreground">
                  <X className="w-4 h-4 lg:w-5 lg:h-5 text-red-400/60 flex-shrink-0" />
                  <span className="text-xs lg:text-base text-center leading-tight">
                    {feature.others}
                  </span>
                </div>
              </button>
              
              {/* Expanded Details */}
              <motion.div
                initial={false}
                animate={{ 
                  height: expandedRow === index ? 'auto' : 0,
                  opacity: expandedRow === index ? 1 : 0
                }}
                className="overflow-hidden bg-teal-500/5 border-b border-white/5"
              >
                <div className="p-4 lg:p-6 text-xs lg:text-sm text-muted-foreground">
                  {feature.details}
                </div>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
